import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { 
    getAdminsForServerAPI, 
    getEmployeesForServerAPI,
    getServerDetailsAPI, 
    toggleUserStatusAPI,
    removeUserFromServerAPI,
    fetchServersAPI,
    bulkCloneStaffAPI,
    deleteUserAPI
} from '../services/serverService';
import AddAdminModal from '../components/AddAdminModal';
import BulkUploadModal from '../components/BulkUploadModal';
import { useAuth } from '../context/AuthContext';
import { hasPermission } from '../config/permissions';

// --- ACCEPT userRole AS A PROP ---
const AdminListPage = () => {
    const { userRole } = useAuth();
    const { serverId } = useParams();
    const location = useLocation();
    const [admins, setAdmins] = useState([]);
    const [serverData, setServerData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    
    const [modalState, setModalState] = useState(null);
    const [currentLimits, setCurrentLimits] = useState(null);

    // --- BULK CLONE STATES ---
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [isCloning, setIsCloning] = useState(false);
    const [showCloneModal, setShowCloneModal] = useState(false);
    const [availableServers, setAvailableServers] = useState([]);
    const [targetServerId, setTargetServerId] = useState('');
    const [showBulkUpload, setShowBulkUpload] = useState(false);

    const canEdit = hasPermission(userRole, 'CAN_EDIT_DATA');
    const canDelete = hasPermission(userRole, 'CAN_DELETE_DATA');

    useEffect(() => {
        if (location.state?.openAddModal) {
            setModalState('add');
            if (location.state?.currentLimits) {
                setCurrentLimits(location.state.currentLimits);
            }
            window.history.replaceState({}, document.title)
        }
    }, [location.state]);

    useEffect(() => {
        if (!serverId) return;
        fetchAllData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serverId]);

    const fetchAllData = async () => {
        try {
            setIsLoading(true);
            // Fetch BOTH to get an accurate global active count
            const [adminResponse, employeeResponse, serverResponse] = await Promise.all([
                getAdminsForServerAPI(serverId),
                getEmployeesForServerAPI(serverId),
                getServerDetailsAPI(serverId)
            ]);

            const currentAdmins = adminResponse.data.data;
            const currentEmployees = employeeResponse.data.data;
            const currentServer = serverResponse.data.data;
            
            setAdmins(currentAdmins);
            setServerData(currentServer);
            
            // Calculate only ACTIVE staff using universal legacy field
            const isUserActive = (u) => u.active !== undefined ? u.active === true : u.status === true;
            
            const activeA = currentAdmins.filter(isUserActive).length;
            const activeE = currentEmployees.filter(isUserActive).length;

            setCurrentLimits({
                currentCount: activeA + activeE,
                maxLimit: currentServer.maxUsers || 0 
            });
            
        } catch (error) {
            console.error("Failed to fetch page data", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseModal = () => {
        setModalState(null);
    };

    // --- CLONE ACTIONS ---
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const visibleIds = admins.map(u => u.username || u.id);
            setSelectedUserIds(visibleIds);
        } else setSelectedUserIds([]);
    };

    const handleSelectRow = (userId) => {
        setSelectedUserIds(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
    };

    const handleOpenCloneModal = async () => {
        if (selectedUserIds.length === 0) return alert("Select at least one user to clone.");
        setIsLoading(true);
        try {
            const response = await fetchServersAPI();
            let serversArray = [];
            const payload = response.data;
            if (Array.isArray(payload)) serversArray = payload;
            else if (payload?.data && Array.isArray(payload.data)) serversArray = payload.data;
            else if (payload?.servers && Array.isArray(payload.servers)) serversArray = payload.servers;
            else if (payload?.data?.servers && Array.isArray(payload.data.servers)) serversArray = payload.data.servers;

            if (!Array.isArray(serversArray)) throw new Error("Could not parse server list.");

            const servers = serversArray.filter(s => (s.server_id || s.id) !== serverId);
            setAvailableServers(servers);
            setTargetServerId('');
            setShowCloneModal(true);
        } catch (error) { alert("Failed to load server list."); } 
        finally { setIsLoading(false); }
    };

    const executeClone = async () => {
        if (!targetServerId) return alert("Please select a target server.");
        const confirmClone = window.confirm(`Clone ${selectedUserIds.length} admins to selected server?\n\nProceed?`);
        if (!confirmClone) return;

        setIsCloning(true);
        setShowCloneModal(false); 
        
        try {
            const response = await bulkCloneStaffAPI({ targetServerId, sourceServerId: serverId, userIds: selectedUserIds, role: 'admin' });
            const { successCount, clonedUsers, errors } = response.data.data;

            if (successCount > 0) {
                alert(`Successfully cloned ${successCount} admins. Downloading credentials CSV...`);
                const headers = ['Full Name', 'New Username', 'Email', 'Temporary Password'];
                const rows = clonedUsers.map(u => [`"${u.fullName}"`, `"${u.newUsername}"`, `"${u.email}"`, `"${u.plainTextPassword}"`]);
                const csvContent = [headers.join(','), ...rows.map(e => e.join(','))].join("\n");
                
                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = `Cloned_Admins_${targetServerId}.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
            if (errors && errors.length > 0) alert(`Warning: ${errors.length} failed to clone. Check console.`);
            setSelectedUserIds([]);
        } catch (error) { alert("Bulk cloning failed: " + (error.message || "Internal Error")); } 
        finally { setIsCloning(false); }
    };

    const renderCloneModal = () => (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
            <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '8px', width: '400px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                <h3 style={{ marginTop: 0, color: '#2d3748' }}>Select Destination Server</h3>
                <p style={{ color: '#718096', fontSize: '14px', marginBottom: '20px' }}>Choose where to clone the {selectedUserIds.length} selected admins.</p>
                
                <select 
                    value={targetServerId}
                    onChange={(e) => setTargetServerId(e.target.value)}
                    style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #cbd5e0', marginBottom: '20px', fontSize: '15px' }}
                >
                    <option value="" disabled>-- Select a Server --</option>
                    {availableServers.map(s => {
                        const sId = s.server_id || s.id;
                        const sName = s.companyName || s.server_name || s.name || sId;
                        return (
                            <option key={sId} value={sId}>{sName}</option>
                        );
                    })}
                </select>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                    <button onClick={() => setShowCloneModal(false)} style={{ padding: '8px 16px', border: '1px solid #cbd5e0', background: 'white', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
                    <button onClick={executeClone} disabled={!targetServerId} style={{ padding: '8px 16px', border: 'none', background: targetServerId ? '#3182ce' : '#a0aec0', color: 'white', borderRadius: '4px', cursor: targetServerId ? 'pointer' : 'not-allowed', fontWeight: 'bold' }}>Confirm Clone</button>
                </div>
            </div>
        </div>
    );
    
    const handleToggleStatus = async (admin) => {
        const isActiveUser = admin.active !== undefined ? admin.active === true : admin.status === true;
        const actionText = isActiveUser ? "disable" : "enable";
        
        const reason = window.prompt(`Are you sure you want to ${actionText} ${admin.fullName}?\n\nPlease enter a reason for the Audit Log (Required):`);
        
        if (reason === null) return; // User clicked Cancel
        if (reason.trim() === "") {
            return alert("Action cancelled: A reason is required.");
        }

        try {
            await toggleUserStatusAPI({ 
                userId: admin.username || admin.id, 
                uid: admin.uid || admin.id, 
                serverId: serverId, 
                role: 'admin',
                reason: reason.trim() 
            });
            fetchAllData();
        } catch (error) {
            console.error("Failed to toggle admin status", error);
            alert("Could not update status.");
        }
    };

    const handleRemoveUser = async (admin) => {
        const reason = window.prompt(`Are you sure you want to unlink ${admin.fullName} from this server?\n\nThis will NOT delete their global profile.\nPlease enter a reason (Required):`);
        
        if (reason === null) return;
        if (reason.trim() === "") {
            return alert("Action cancelled: A reason is required.");
        }

        try {
            await removeUserFromServerAPI({ userId: admin.username || admin.id, serverId, role: 'admin', reason: reason.trim() });
            fetchAllData(); 
            alert(`${admin.fullName} removed successfully.`);
        } catch (error) {
            console.error("Failed to remove user from server", error);
            alert("Could not remove user. Please try again.");
        }
    };

    const handleDeleteUser = async (admin) => {
        const confirm1 = window.confirm(`WARNING: Are you sure you want to PERMANENTLY DELETE Admin ${admin.fullName}? This cannot be undone.`);
        if (!confirm1) return;
        
        const reason = window.prompt("This will PERMANENTLY DELETE them from THIS server ONLY. Please enter a reason for the Audit Log (Required):");
        
        if (reason === null) return;
        if (reason.trim() === "") {
            return alert("Action cancelled: A reason is required for permanent deletion.");
        }

        try {
            await deleteUserAPI({ userId: admin.username || admin.id, serverId: serverId, role: 'admin', reason: reason.trim() });
            fetchAllData();
            alert("Admin permanently deleted.");
        } catch (error) {
            console.error("Failed to delete admin", error);
            alert("Error deleting: " + (error.response?.data?.message || error.message));
        }
    };

    return (
        <div className="page-container">
            {modalState && (
                <AddAdminModal serverId={serverId} closeModal={handleCloseModal} refreshList={fetchAllData} adminToEdit={modalState !== 'add' ? modalState : null} currentLimits={currentLimits} existingUsers={admins} />
            )}

            {showCloneModal && renderCloneModal()}

            {showBulkUpload && (
                <BulkUploadModal 
                    serverId={serverId} 
                    role="admin"
                    closeModal={() => setShowBulkUpload(false)} 
                    refreshList={fetchAllData} 
                />
            )}

            <div className="page-header">
                <h1>Admins List for <span style={{ color: 'var(--primary-blue)' }}>{serverData?.companyName || 'Server'}</span></h1>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    {canEdit && (
                        <>
                            <button className="add-button" onClick={() => setModalState('add')}>+ Add New Admin</button>
                            <button className="add-button" onClick={() => setShowBulkUpload(true)} style={{ backgroundColor: '#2b6cb0' }}>Bulk Import via CSV</button>
                        </>
                    )}
                </div>
            </div>

            <Link to={`/servers/${serverId}`} className="back-link" style={{ marginBottom: '1rem' }}>&larr; Back to Server Details</Link>

            {selectedUserIds.length > 0 && canDelete && (
                <div style={{ backgroundColor: '#ebf8ff', padding: '15px', borderRadius: '8px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #bee3f8' }}>
                    <div style={{ color: '#2b6cb0', fontWeight: 'bold' }}>{selectedUserIds.length} admin(s) selected</div>
                    <button onClick={handleOpenCloneModal} disabled={isCloning} style={{ backgroundColor: '#3182ce', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', fontWeight: 'bold', cursor: isCloning ? 'not-allowed' : 'pointer' }}>
                        {isCloning ? 'Cloning & Generating Passwords...' : 'Clone Selected to New Server'}
                    </button>
                </div>
            )}

            <div className="data-table-container">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th style={{ width: '60px', textAlign: 'center', padding: '0 10px', overflow: 'visible', textOverflow: 'clip' }}>
                                <input 
                                    type="checkbox"
                                    onChange={handleSelectAll}
                                    checked={admins.length > 0 && selectedUserIds.length === admins.length}
                                    style={{ cursor: 'pointer', transform: 'scale(1.2)' }}
                                />
                            </th>
                            <th>Username</th>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Phone Number</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr><td colSpan="7" className="no-data-cell">Loading...</td></tr>
                        ) : admins.length > 0 ? (
                            admins.map((admin) => {
                                const isActiveUser = admin.active !== undefined ? admin.active === true : admin.status === true;

                                return (
                                    <tr key={admin.id} onClick={() => navigate(`/servers/${serverId}/admins/${admin.id}`)} style={{ cursor: 'pointer', backgroundColor: selectedUserIds.includes(admin.username || admin.id) ? '#f0f9ff' : 'transparent' }}>
                                        <td style={{ textAlign: 'center', padding: '0 10px', overflow: 'visible', textOverflow: 'clip' }} onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                checked={selectedUserIds.includes(admin.username || admin.id)}
                                                onChange={() => handleSelectRow(admin.username || admin.id)}
                                                style={{ cursor: 'pointer', transform: 'scale(1.2)' }}
                                            />
                                        </td>
                                        <td>{admin.username}</td>
                                        <td>{admin.fullName}</td>
                                        <td>{admin.email}</td>
                                        <td>{admin.phoneNumber}</td>
                                        <td><span className={isActiveUser ? 'status-active' : 'status-inactive'}>{isActiveUser ? 'Active' : 'Suspended'}</span></td>
                                        <td className="actions-cell">
                                            {canEdit ? (
                                                <div className="actions-wrapper" onClick={(e) => e.stopPropagation()}>
                                                    <button className="action-button edit" onClick={() => setModalState(admin)}>Edit</button>
                                                    <button className={`action-button ${isActiveUser ? 'danger' : 'success'}`} onClick={() => handleToggleStatus(admin)}>{isActiveUser ? 'Disable' : 'Enable'}</button>
                                                    
                                                    {canDelete && (
                                                        <>
                                                            <button className="action-button" style={{ backgroundColor: '#f0ad4e', color: 'white', borderColor: '#eea236' }} onClick={() => handleRemoveUser(admin)} title="Remove from this server only">Unlink</button>
                                                            <button className="action-button delete" onClick={() => handleDeleteUser(admin)} title="Permanently Delete">Delete</button>
                                                        </>
                                                    )}
                                                </div>
                                            ) : (
                                                <span style={{ color: '#999', fontSize: '0.85rem', fontStyle: 'italic' }}>View Only</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr><td colSpan="7" className="no-data-cell">No Admins Found</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AdminListPage;